import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Appointment, AvailabilitySlot } from '../../types/appointment.types';
import { appointmentService } from '../../services/appointmentService';
import { useToast } from '../../context/ToastContext';
import { getLocalDateString } from '../../utils/dateUtils';

interface AppointmentManageModalProps {
    appointment: Appointment | null;
    isOpen: boolean;
    onClose: () => void;
    onAppointmentUpdated: () => void;
}

type ModalView = 'main' | 'cancel' | 'reschedule';

const AppointmentManageModal: React.FC<AppointmentManageModalProps> = ({
    appointment,
    isOpen,
    onClose,
    onAppointmentUpdated
}) => {
    const [currentView, setCurrentView] = useState<ModalView>('main');
    const [cancelReason, setCancelReason] = useState('');
    const [rescheduleReason, setRescheduleReason] = useState('');
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleTime, setRescheduleTime] = useState('');
    const [availableSlots, setAvailableSlots] = useState<AvailabilitySlot[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();

    // Check time status for warnings and actions
    const getTimeStatus = () => {
        if (!appointment) return { isExpired: false, lessThan24Hours: false };
        const appointmentDateTime = new Date(`${appointment.appointmentDate}T${appointment.startTime}`);
        const now = new Date();
        const diff = appointmentDateTime.getTime() - now.getTime();
        const hoursUntil = diff / (1000 * 60 * 60);

        return {
            isExpired: diff < 0,
            lessThan24Hours: hoursUntil >= 0 && hoursUntil < 24
        };
    };

    const resetState = () => {
        setCurrentView('main');
        setCancelReason('');
        setRescheduleReason('');
        setRescheduleDate('');
        setRescheduleTime('');
        setAvailableSlots([]);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    // Load available slots when date changes
    useEffect(() => {
        if (rescheduleDate && appointment) {
            loadAvailableSlots();
        }
    }, [rescheduleDate]);

    const loadAvailableSlots = async () => {
        if (!appointment || !rescheduleDate) return;

        setLoadingSlots(true);
        try {
            const slots = await appointmentService.getAvailableSlots(appointment.barberId, rescheduleDate);
            setAvailableSlots(slots);
        } catch (error) {
            console.error('Error loading slots:', error);
            showToast('Error al cargar horarios disponibles', 'error');
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleCancel = async () => {
        if (!appointment || !cancelReason.trim()) {
            showToast('Debe ingresar un motivo de cancelación', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await appointmentService.cancelAppointment(appointment.id, cancelReason);
            showToast('Turno cancelado. Se notificó al barbero por email.', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error: any) {
            console.error('Error cancelling appointment:', error);
            if (error.response?.data?.message?.includes('LESS_THAN_24_HOURS')) {
                showToast('No puede cancelar con menos de 24 horas de anticipación. Contacte al barbero.', 'error');
            } else {
                showToast('Error al cancelar el turno', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleReschedule = async () => {
        if (!appointment) return;
        if (!rescheduleDate || !rescheduleTime) {
            showToast('Debe seleccionar fecha y horario', 'warning');
            return;
        }
        if (!rescheduleReason.trim()) {
            showToast('Debe ingresar un motivo de reprogramación', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await appointmentService.rescheduleAppointment(
                appointment.id,
                rescheduleDate,
                rescheduleTime,
                rescheduleReason
            );
            showToast('Turno reprogramado. Se notificó al barbero por email.', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error: any) {
            console.error('Error rescheduling appointment:', error);
            if (error.response?.data?.message?.includes('LESS_THAN_24_HOURS')) {
                showToast('No puede reprogramar con menos de 24 horas de anticipación. Contacte al barbero.', 'error');
            } else if (error.response?.data?.message?.includes('no está disponible')) {
                showToast('El horario seleccionado ya no está disponible', 'error');
            } else {
                showToast('Error al reprogramar el turno', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!appointment) return null;

    const { isExpired, lessThan24Hours } = getTimeStatus();
    const cannotModify = isExpired || lessThan24Hours;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={
                currentView === 'main' ? 'Gestionar Turno' :
                    currentView === 'cancel' ? 'Cancelar Turno' : 'Reprogramar Turno'
            }
        >
            {/* Main View */}
            {currentView === 'main' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Appointment Details */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        padding: '1.25rem',
                        borderRadius: '0.75rem',
                        border: '1px solid #e2e8f0'
                    }}>
                        <p style={{ margin: '0.25rem 0', fontWeight: '800', fontSize: '1.1rem', color: '#1e293b' }}>
                            📅 {appointment.appointmentDate} <span style={{ color: '#3b82f6', marginLeft: '0.5rem' }}>{appointment.startTime.substring(0, 5)} hs</span>
                        </p>
                        <p style={{ margin: '0.5rem 0 0.25rem 0', fontWeight: '600', color: '#334155' }}>
                            ✂️ {appointment.barberName}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                                📍 {appointment.branchName}
                            </p>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                                💇 {appointment.serviceDescription || 'Corte de Cabello'}
                            </p>
                        </div>
                    </div>

                    {/* 24 Hour Warning / Expired Warning */}
                    {isExpired ? (
                        <div style={{
                            backgroundColor: '#fff1f2',
                            padding: '1rem',
                            borderRadius: '0.75rem',
                            border: '1px solid #fda4af',
                            borderLeft: '4px solid #e11d48'
                        }}>
                            <p style={{ margin: 0, fontWeight: '700', color: '#9f1239' }}>
                                🕒 Este turno ya pasó su horario
                            </p>
                            <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.85rem', color: '#9f1239', lineHeight: '1.4' }}>
                                El turno se encuentra fuera de tiempo para modificaciones. Si el servicio se realizó, el barbero lo marcará como completado pronto.
                            </p>
                        </div>
                    ) : lessThan24Hours && (
                        <div style={{
                            backgroundColor: '#fffbeb',
                            padding: '1rem',
                            borderRadius: '0.75rem',
                            border: '1px solid #fde68a',
                            borderLeft: '4px solid #d97706'
                        }}>
                            <p style={{ margin: 0, fontWeight: '700', color: '#92400e' }}>
                                ⚠️ Faltan menos de 24 horas para tu turno
                            </p>
                            <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.85rem', color: '#92400e', lineHeight: '1.4' }}>
                                No puedes modificar este turno desde aquí. Por favor contacta directamente al barbero
                                para cualquier cambio.
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {!cannotModify && (
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                className="btn btn-outline"
                                onClick={() => setCurrentView('reschedule')}
                                style={{ flex: 1 }}
                            >
                                📅 Reprogramar
                            </button>
                            <button
                                className="btn"
                                onClick={() => setCurrentView('cancel')}
                                style={{
                                    flex: 1,
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                ❌ Cancelar
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Cancel View */}
            {currentView === 'cancel' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        ← Volver
                    </button>

                    <div style={{
                        backgroundColor: '#fee2e2',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #fecaca'
                    }}>
                        <p style={{ margin: 0, color: '#991b1b', fontWeight: '600' }}>
                            ⚠️ Estás por cancelar tu turno del {appointment.appointmentDate} a las {appointment.startTime.substring(0, 5)}
                        </p>
                    </div>

                    <div>
                        <label className="form-label">📝 Motivo de Cancelación *</label>
                        <textarea
                            className="form-input"
                            placeholder="Ej: Tengo un imprevisto, no puedo asistir, etc."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={3}
                            style={{ resize: 'vertical' }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Se notificará al barbero por email.
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            className="btn btn-outline"
                            onClick={() => setCurrentView('main')}
                            disabled={submitting}
                            style={{ flex: 1 }}
                        >
                            Volver
                        </button>
                        <button
                            className="btn"
                            onClick={handleCancel}
                            disabled={submitting || !cancelReason.trim()}
                            style={{
                                flex: 1,
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            {submitting ? 'Cancelando...' : 'Confirmar Cancelación'}
                        </button>
                    </div>
                </div>
            )}

            {/* Reschedule View */}
            {currentView === 'reschedule' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        ← Volver
                    </button>

                    <div style={{
                        backgroundColor: '#eff6ff',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #bfdbfe'
                    }}>
                        <p style={{ margin: 0, color: '#1e40af', fontSize: '0.875rem' }}>
                            📅 Selecciona una nueva fecha y horario para tu turno con {appointment.barberName}
                        </p>
                    </div>

                    <div>
                        <label className="form-label">Nueva Fecha *</label>
                        <input
                            type="date"
                            className="form-input"
                            value={rescheduleDate}
                            onChange={(e) => {
                                setRescheduleDate(e.target.value);
                                setRescheduleTime('');
                            }}
                            min={getLocalDateString()}
                        />
                    </div>

                    {rescheduleDate && (
                        <div>
                            <label className="form-label">Horarios Disponibles *</label>
                            {loadingSlots ? (
                                <p>Cargando horarios...</p>
                            ) : availableSlots.length === 0 ? (
                                <p style={{ color: '#ef4444' }}>No hay horarios disponibles para esta fecha</p>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: '0.5rem',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    padding: '0.5rem',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '0.5rem'
                                }}>
                                    {availableSlots.filter(s => s.available).map((slot, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setRescheduleTime(slot.time)}
                                            style={{
                                                padding: '0.6rem 0.4rem',
                                                border: rescheduleTime === slot.time
                                                    ? '1px solid #3b82f6'
                                                    : '1px solid #e2e8f0',
                                                borderRadius: '0.5rem',
                                                backgroundColor: rescheduleTime === slot.time
                                                    ? '#eff6ff'
                                                    : 'white',
                                                color: rescheduleTime === slot.time
                                                    ? '#2563eb'
                                                    : '#475569',
                                                cursor: 'pointer',
                                                fontSize: '0.8rem',
                                                fontWeight: '700',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {slot.time}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {rescheduleDate && rescheduleTime && (
                        <div>
                            <label className="form-label">📝 Motivo de Reprogramación *</label>
                            <textarea
                                className="form-input"
                                placeholder="Ej: Cambio en mis horarios, viaje programado, etc."
                                value={rescheduleReason}
                                onChange={(e) => setRescheduleReason(e.target.value)}
                                rows={3}
                                style={{ resize: 'vertical' }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Se notificará al barbero por email.
                            </p>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            className="btn btn-outline"
                            onClick={() => setCurrentView('main')}
                            disabled={submitting}
                            style={{ flex: 1 }}
                        >
                            Volver
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleReschedule}
                            disabled={submitting || !rescheduleDate || !rescheduleTime || !rescheduleReason.trim()}
                            style={{ flex: 1 }}
                        >
                            {submitting ? 'Procesando...' : 'Confirmar Nueva Fecha'}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AppointmentManageModal;
